import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adApi } from '../../api/ad.api';
import { ActivationData } from '../../types/Ad.api.type';
import { Card, CardContent, CardHeader } from '../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Zap, Users } from 'lucide-react';
import { RetentionDashboard } from './retention/RetentionDashboard';
import { ActiveUserList } from './ActiveUserList';

type SubTab = 'analysis' | 'activeUsers';

/**
 * AARRR 서비스 분석 대시보드
 */
export const ServiceAnalyticsDashboard = () => {
  const [subTab, setSubTab] = useState<SubTab>('analysis');

  const { data: activation, isLoading: loadingAct } = useQuery<ActivationData>({
    queryKey: ['analytics', 'activation'],
    queryFn: () => adApi.getActivation().then(res => res.data),
    enabled: subTab === 'analysis',
  });

  return (
    <div className="space-y-6">
      {/* 서브탭 */}
      <div className="flex gap-1 bg-[#f3f3f5] rounded-lg p-1 w-fit">
        <button
          onClick={() => setSubTab('analysis')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            subTab === 'analysis'
              ? 'bg-white text-[#030213] shadow-sm'
              : 'text-[#6B7280] hover:text-[#030213]'
          }`}
        >
          <Zap className="w-4 h-4" />
          서비스 분석
        </button>
        <button
          onClick={() => setSubTab('activeUsers')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            subTab === 'activeUsers'
              ? 'bg-white text-[#030213] shadow-sm'
              : 'text-[#6B7280] hover:text-[#030213]'
          }`}
        >
          <Users className="w-4 h-4" />
          활성 사용자
        </button>
      </div>

      {/* 서비스 분석 탭 */}
      {subTab === 'analysis' && (
        <>
          {loadingAct ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              로딩 중...
            </div>
          ) : (
            <div className="space-y-8">
              {/* ============ Activation (활성화) ============ */}
              <section className="mt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  <h3 className="text-lg font-bold text-gray-900">Activation (활성화)</h3>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <MetricCard label="첫 투표 완료율" value={`${(activation?.firstVoteRate ?? 0).toFixed(1)}%`} sub={`${activation?.votedUsers ?? 0} / ${activation?.totalRegistered ?? 0}`} />
                  <MetricCard label="첫 출석체크율" value={`${(activation?.firstAttendanceRate ?? 0).toFixed(1)}%`} sub={`${activation?.attendedUsers ?? 0} / ${activation?.totalRegistered ?? 0}`} />
                  <MetricCard label="첫 설문 작성율" value={`${(activation?.firstQuestionRate ?? 0).toFixed(1)}%`} sub={`${activation?.questionCreators ?? 0} / ${activation?.totalRegistered ?? 0}`} />
                  <MetricCard label="활성화 소요시간" value={`${(activation?.avgActivationHours ?? 0).toFixed(1)}시간`} />
                </div>

                {/* 퍼널 바 차트 */}
                <Card>
                  <CardHeader className="pb-2">
                    <h4 className="text-sm font-semibold text-gray-700">활성화 퍼널</h4>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart
                        data={[
                          { name: '가입', value: activation?.totalRegistered ?? 0 },
                          { name: '첫 투표', value: activation?.votedUsers ?? 0 },
                          { name: '첫 출석체크', value: activation?.attendedUsers ?? 0 },
                          { name: '첫 설문 작성', value: activation?.questionCreators ?? 0 },
                        ]}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 5, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis type="number" stroke="#6B7280" style={{ fontSize: '12px' }} />
                        <YAxis type="category" dataKey="name" stroke="#6B7280" style={{ fontSize: '12px' }} width={80} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '12px' }}
                        />
                        <Bar dataKey="value" fill="#F59E0B" radius={[0, 4, 4, 0]} name="사용자 수" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </section>

              {/* ============ Retention (리텐션) ============ */}
              <section>
                <RetentionDashboard />
              </section>
            </div>
          )}
        </>
      )}

      {/* 활성 사용자 탭 */}
      {subTab === 'activeUsers' && <ActiveUserList />}
    </div>
  );
};

/** 숫자 메트릭 카드 */
const MetricCard = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <Card>
    <div className="p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </Card>
);
